/**
 * Checkout kit: the one implementation of what "Gå til betaling" writes, shared by the booking module
 * (assets/myselfie-booking-calendar.js, every "Book din Myselfie") and the DK cart page (assets/mys-cart.js).
 * Load it before either of them (defer, earlier in the document). Vanilla ES5, no DOM state of its own.
 *
 *   window.myselfie.checkoutKit = {
 *     cartJson, cartPost, cartGet, wait, rootUrl     Ajax cart API with the store's 429 budget
 *     validEan, eanProblem, billingIssues            Faktura / EAN validation (same texts everywhere)
 *     reasonIssues, reasonsFor                       occasion ("Anledning") rules
 *     termsIssue, termsDocument                      general terms vs lease terms
 *     billingData                                    customer type, payment, invoice note, discount code, lease plan
 *     checkoutAttributes, noteFrom                   cart attributes + note written right before checkout
 *     addAutomaticLines                              free props / prop holder (sections/main-cart.liquid rules)
 *     syncFreeProps, boothCount, isAutoLine          one free-props unit per booth, none without a booth
 *     freePropsOk, FREE_PROPS_TAG                    is the free-props line right; the query tag on the sync's own requests
 *     paidCount, cartDetail                          cart count = quantities of the lines that cost money; "myselfie:cart" detail
 *     serial                                         one cart writer at a time for the page-level writers (cart rules, season guard)
 *     orderNoLimits, validOrderNo, orderNoMessage    order number rules (DK cart section settings)
 *     orderRule, orderRuleText, orderRuleMin,        when a cart needs an order number (theme settings "Ordrenummer-regel":
 *     checkoutAllowed                                min value before discounts, exempt / required SKU prefixes)
 *     printKey, printModeOf, printTypeIndex/Value    print type (Polaroid = P, Strip/Fotostribe = S), print_mode
 *     logoCode, logoPrintKey, pickLogo, logoName     "Logo på print" variant by logo (ML/NL/EL) + print type
 *     checkoutUrl                                    /checkout, or /discount/CODE?redirect=/checkout
 *     ensureSeasons                                  season guard right before checkout (assets/myselfie-seasons.js)
 *   }
 */
(function () {
  'use strict';

  window.myselfie = window.myselfie || {};
  if (window.myselfie.checkoutKit) return;

  /* The store rate-limits in windows of ~30 s: 429s are retried after 1, 2, 4, 8 and 15 s. */
  var RETRY_DELAYS = [1000, 2000, 4000, 8000, 15000];
  /* Adds that must not open a cart drawer app (Upcart reads these two parameters). */
  var QUIET_ADD = 'cart/add.js?upcart=1&opens_cart=never';
  /* Marks the free-props sync's own requests (Shopify ignores the query), so the global cart rules
     (assets/myselfie-cart-rules.js) never react to them: no loops. */
  var FREE_PROPS_TAG = 'myselfie=free-props';

  function rootUrl(path) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return root + path;
  }
  function wait(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }
  function kr(ore) {
    return Math.round(ore / 100).toLocaleString('da-DK') + ' kr';
  }
  /**
   * Reads a cart API response. Shopify answers with an HTML page when it rate-limits (429) or
   * fails, so never hand a parser error to the customer.
   */
  function cartJson(r) {
    var ct = (r.headers && r.headers.get('content-type')) || '';
    if (r.status === 429) return Promise.reject({ status: 429, message: 'Der er travlt i webshoppen lige nu. Vent et øjeblik, og prøv igen.' });
    // /cart.js is not always labelled as JSON; only an HTML page means an error/limit page
    if (ct.indexOf('text/html') > -1) return Promise.reject({ status: r.status, message: 'Kurven svarede ikke som forventet. Prøv igen om et øjeblik.' });
    return r.json().then(function (body) {
      if (!r.ok) throw { status: r.status, message: body.description || body.message || 'Kurven kunne ikke opdateres.' };
      return body;
    });
  }
  /** Wait before retrying a 429: the server's Retry-After (max 20 s), else the budget above. */
  function retryDelay(r, attempt) {
    var after = Number(r.headers && r.headers.get('Retry-After'));
    return after > 0 ? Math.min(20000, after * 1000) : RETRY_DELAYS[attempt];
  }
  /** POST JSON to the Ajax cart API; 429s are retried within the budget before giving up. */
  function cartPost(path, body, retries) {
    var left = retries == null ? RETRY_DELAYS.length : retries;
    var attempt = 0;
    function go() {
      return fetch(rootUrl(path), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body || {}),
      }).then(function (r) {
        if (r.status === 429 && attempt < left) {
          var ms = retryDelay(r, attempt);
          attempt++;
          return wait(ms).then(go);
        }
        return cartJson(r);
      });
    }
    return go();
  }
  function cartGet() {
    var attempt = 0;
    function go() {
      return fetch(rootUrl('cart.js'), { headers: { Accept: 'application/json' }, cache: 'no-store' }).then(function (r) {
        if (r.status === 429 && attempt < RETRY_DELAYS.length) {
          var ms = retryDelay(r, attempt);
          attempt++;
          return wait(ms).then(go);
        }
        return cartJson(r);
      });
    }
    return go();
  }

  /* ---------- billing ---------- */

  /** EAN-13 / GLN: 13 digits with a valid GS1 check digit. */
  function validEan(value) {
    if (!/^\d{13}$/.test(value)) return false;
    var sum = 0;
    for (var i = 0; i < 12; i++) sum += Number(value[i]) * (i % 2 ? 3 : 1);
    return (10 - (sum % 10)) % 10 === Number(value[12]);
  }
  /** '' when the EAN field is fine, else the message for the customer. */
  function eanProblem(value) {
    var raw = String(value || '').replace(/\s+/g, '');
    if (!raw) return 'Skriv jeres EAN-nummer – 13 cifre, f.eks. 5790001234560.';
    if (/\D/.test(raw)) return 'EAN-nummeret må kun indeholde tal.';
    if (raw.length !== 13) return 'EAN-nummeret skal være 13 cifre – der står ' + raw.length + '.';
    if (!validEan(raw)) return 'Nummeret ser ikke ud til at være et gyldigt EAN-nummer. Tjek, at alle cifre er skrevet rigtigt.';
    return '';
  }
  /** Problems of the payment fields. pay: 'standard' | 'faktura' | 'ean' (the method in effect). */
  function billingIssues(pay, invoice, ean) {
    var out = [];
    if (pay === 'faktura' && String(invoice || '').trim().length < 10) {
      out.push({ key: 'invoice', step: 'billing', label: 'Udfyld fakturaoplysninger', message: 'Skriv fakturaoplysningerne – mindst firmanavn, CVR-nr. og e-mail til fakturaen.' });
    }
    if (pay === 'ean') {
      var e = eanProblem(ean);
      if (e) out.push({ key: 'ean', step: 'billing', label: 'Tjek EAN-nummeret', message: e });
    }
    return out;
  }

  /**
   * Cart note, attributes and discount code for the chosen payment method.
   * o: {customer: 'privat'|'erhverv', pay: method in effect, invoice, ean, att, codes: {faktura, ean},
   *     lease: null | {plan: 'full'|'monthly', months, total (øre, the booking's lines), pct, fullCode, monthlyCode}}
   */
  function billingData(o) {
    var pay = o.pay || 'standard';
    var codes = o.codes || {};
    var data = {
      attributes: {
        customer_type: o.customer === 'erhverv' ? 'Erhverv' : 'Privat',
        payment_method: { standard: 'Standard', faktura: 'Faktura', ean: 'EAN' }[pay],
      },
      note: null,
      code: '',
    };
    if (pay === 'faktura') {
      data.note = 'Betaling: Faktura\n' + String(o.invoice || '').trim();
      data.code = codes.faktura || '';
    } else if (pay === 'ean') {
      var ean = String(o.ean || '').replace(/\s+/g, '');
      var att = String(o.att || '').trim();
      data.attributes.ean_number = ean;
      data.attributes.ean_att = att;
      data.note = 'Betaling: EAN\nEAN: ' + ean + (att ? '\nAtt.: ' + att : '');
      data.code = codes.ean || '';
    }
    var l = o.lease;
    if (l) {
      var months = Math.max(1, Number(l.months) || 1);
      if (l.plan === 'monthly') {
        var perMonth = kr(Math.round((l.total || 0) / months)) + ' inkl. moms';
        data.attributes.lease_plan = 'Månedlig faktura';
        data.attributes.lease_months = String(months);
        data.attributes.lease_monthly_amount = perMonth;
        data.note = 'Betaling: Månedlig faktura – ' + months + (months === 1 ? ' måned' : ' måneder') + ' à ' + perMonth + (data.note ? '\n' + data.note : '');
        if (pay === 'standard') data.code = l.monthlyCode || ''; // not reachable: standard is disabled with the monthly plan
      } else {
        var pct = Math.max(0, Math.min(100, Number(l.pct) || 0));
        data.attributes.lease_plan = 'Fuld betaling' + (pct ? ' (' + pct + '% rabat)' : '');
        data.attributes.lease_months = String(months);
        if (pay === 'standard') data.code = l.fullCode || '';
        else if (pct) data.note = (data.note ? data.note + '\n' : '') + 'Langtidsleje: fuld betaling – ' + pct + '% rabat trækkes på fakturaen.';
      }
    }
    return data;
  }

  /* ---------- occasion and terms ---------- */

  function reasonIssues(reason, other) {
    var t = String(other || '').trim();
    if (!reason) return [{ key: 'reason', step: 'reason', label: 'Vælg anledning', message: 'Vælg anledningen til jeres event.' }];
    if (reason === 'andet' && t.length < 3) {
      return [{ key: 'reason-other', step: 'reason', label: 'Beskriv anledningen', message: t ? 'Skriv lidt mere – mindst 3 tegn.' : 'Skriv kort, hvad anledningen er (f.eks. "Sølvbryllup").' }];
    }
    return [];
  }
  /**
   * Occasions offered: fixed keys (event/lease pages) in that order; Erhverv the business list in that
   * order (else everything); Privat everything except the "Erhverv - …" leasing options (r21, r22).
   * catalog: [{key, value, label}]
   */
  function reasonsFor(catalog, b2b, business, fixed) {
    var pick = function (keys) {
      return keys.map(function (k) { return catalog.filter(function (r) { return r.key === k; })[0]; }).filter(Boolean);
    };
    if (fixed && fixed.length) return pick(fixed);
    if (b2b && business && business.length) return pick(business);
    if (b2b) return catalog.slice();
    return catalog.filter(function (r) { return !/^r2[12]$/.test(r.key); });
  }
  function termsIssue(lease) {
    return lease
      ? { key: 'terms', step: 'consent', label: 'Accepter lejebetingelserne', message: 'Sæt kryds for at acceptere lejebetingelserne for langtidsleje.' }
      : { key: 'terms', step: 'consent', label: 'Accepter forretningsbetingelserne', message: 'Sæt kryds for at acceptere Myselfies forretningsbetingelser.' };
  }
  /** Which terms the customer accepted (order attribute terms_document, kept as proof). */
  function termsDocument(lease, doc, url) {
    if (!lease) return 'Forretningsbetingelser';
    return (doc || 'Lejebetingelser for langtidsleje') + ' (' + (url || '') + ')';
  }

  /* ---------- what checkout writes ---------- */

  /**
   * Cart attributes written right before checkout.
   * o: {booking: null | {display: 'DD/MM/YYYY', reason, other}, termsDocument, marketing: bool,
   *     billing: billingData() | null, extra: {} (e.g. orderNo)}
   */
  function checkoutAttributes(o) {
    var attributes = {};
    var b = o.booking;
    if (b) {
      attributes.booking_date = b.display;
      attributes.booking_reason = b.reason;
      attributes.booking_reason_other = b.reason === 'andet' ? b.other : '';
    }
    attributes.terms_accepted = 'Ja';
    attributes.terms_document = o.termsDocument;
    attributes.terms_accepted_at = new Date().toISOString();
    attributes.marketing_consent = o.marketing ? 'Ja' : 'Nej';
    var billing = o.billing;
    if (billing) Object.keys(billing.attributes).forEach(function (k) { attributes[k] = billing.attributes[k]; });
    var extra = o.extra || {};
    Object.keys(extra).forEach(function (k) { attributes[k] = extra[k]; });
    return attributes;
  }
  /** The order note from its parts (billing note first, then e.g. "Bemærkning: …"). */
  function noteFrom(parts) {
    return (parts || []).filter(Boolean).join('\n\n');
  }
  function checkoutUrl(code, target) {
    var to = target || rootUrl('checkout');
    return code ? rootUrl('discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(to)) : to;
  }

  /* ---------- automatic lines: free props per booth, free prop holder ---------- */

  /** True for a free-props / prop-holder / app reward line (never a booth). */
  function isAutoLine(it, c) {
    if (c && c.freeProps && it.variant_id === c.freeProps) return true;
    if (c && c.propHolder && it.variant_id === c.propHolder.id) return true;
    var p = it.properties || {};
    return Object.keys(p).some(function (k) { return k.indexOf('__upcart') === 0; });
  }
  /**
   * The cart count the customer sees (owner 2026-10-05): the quantities of the lines that cost money. Free props, the
   * prop holder, the 0 kr "Myselfie Logo" line and any other 0 kr line are not counted; a paid logo line is.
   * The same rule is server-rendered by snippets/mys-cart-paid-count.liquid (final_line_price > 0).
   */
  function linePrice(it) {
    if (typeof it.final_line_price === 'number') return it.final_line_price;
    if (typeof it.line_price === 'number') return it.line_price;
    if (typeof it.final_price === 'number') return it.final_price * (it.quantity || 0);
    return typeof it.price === 'number' ? it.price * (it.quantity || 0) : 1;
  }
  function paidCount(cart) {
    return ((cart && cart.items) || []).reduce(function (n, it) { return n + (linePrice(it) > 0 ? +it.quantity || 0 : 0); }, 0);
  }
  /** Detail of the document event "myselfie:cart": item_count (Shopify's, kept for compatibility), paid_count, cart. */
  function cartDetail(cart) {
    var full = !!(cart && cart.items && typeof cart.item_count === 'number');
    return {
      item_count: cart && typeof cart.item_count === 'number' ? cart.item_count : 0,
      items_subtotal_price: cart && cart.items_subtotal_price ? cart.items_subtotal_price : 0,
      paid_count: full ? paidCount(cart) : 0,
      cart: full ? cart : null,
    };
  }

  /**
   * One cart writer at a time for writers that have no queue of their own (the global cart rules and the season
   * guard on pages without the booking module or the cart page). Resolves/rejects with the job.
   */
  var serialChain = Promise.resolve();
  function serial(job) {
    var run = serialChain.then(function () { return job(); });
    serialChain = run.catch(function () {});
    return run;
  }

  /** Number of Myselfie booths in the cart (quantity of the camera-type lines, bundles from before a split included). */
  function boothCount(cart, c) {
    return ((cart && cart.items) || []).filter(function (it) { return c && c.cameraType && it.product_type === c.cameraType && !isAutoLine(it, c); })
      .reduce(function (n, it) { return n + it.quantity; }, 0);
  }
  /** True when the free-props line is right for the cart: one line with quantity = booths, or none without a booth. */
  function freePropsOk(cart, c) {
    if (!c || !c.freeProps || !cart || !cart.items) return true;
    var want = boothCount(cart, c);
    var lines = cart.items.filter(function (it) { return it.variant_id === c.freeProps; });
    return want ? lines.length === 1 && lines[0].quantity === want : !lines.length;
  }
  /**
   * Free props (owner 2026-10-05): exactly one unit per Myselfie booth - one line, quantity = the number of booths;
   * no booth -> no free-props line. An app reward line (Upcart "__upcartFreeGift") is the line kept when present.
   * c: {cameraType, freeProps}. Resolves the (fresh) cart; never rejects (the cart as it was on errors).
   */
  function syncFreeProps(cart, c) {
    c = c || {};
    var start = cart ? Promise.resolve(cart) : cartGet();
    return start.then(function (cur) {
      if (!c.freeProps) return cur;
      var want = boothCount(cur, c);
      var lines = (cur.items || []).filter(function (it) { return it.variant_id === c.freeProps; });
      var keep = lines.filter(function (it) { return it.properties && it.properties.__upcartFreeGift; })[0] || lines[0] || null;
      var ops = [];
      lines.forEach(function (it) {
        if (it === keep && want > 0) { if (it.quantity !== want) ops.push({ id: it.key, quantity: want }); }
        else ops.push({ id: it.key, quantity: 0 });
      });
      if (!ops.length && (keep || !want)) return cur;
      var chain = Promise.resolve(cur);
      // removals/quantities one change.js at a time (line keys stay valid), the add last
      ops.forEach(function (op) { chain = chain.then(function () { return cartPost('cart/change.js?' + FREE_PROPS_TAG, op); }); });
      if (!keep && want > 0) {
        chain = chain.then(function () { return cartPost(QUIET_ADD + '&' + FREE_PROPS_TAG, { items: [{ id: c.freeProps, quantity: want }] }); }).then(cartGet);
      }
      return chain.then(function (next) { return next && next.items ? next : cartGet(); });
    }).catch(function () {
      return cart || cartGet().catch(function () { return cart; });
    });
  }

  /* ---------- "Inkluderet: 400 prints" line item property (owner 2026-10-07) ----------
     window.myselfie.printInfo {key, value ("__PRINTS__ prints"), rollKey, rollValue, perRoll}: emitted by
     snippets/myselfie-cart-rules.liquid (DK only; no config = nothing happens). Visible (no underscore) so the cart, the checkout,
     the order e-mail and the admin show it. Matching code (live-cart pass, line classification) ignores these keys. */
  function printInfoConfig() {
    var c = window.myselfie && window.myselfie.printInfo;
    return c && c.key ? c : null;
  }
  function printInfoKeys() {
    var c = printInfoConfig();
    return c ? [c.key, c.rollKey].filter(Boolean) : [];
  }
  /** props without the print info keys. */
  function stripPrintInfo(props) {
    var keys = printInfoKeys();
    var out = {};
    Object.keys(props || {}).forEach(function (k) { if (keys.indexOf(k) < 0) out[k] = props[k]; });
    return out;
  }
  /**
   * The property a line with this SKU gets, as {name: value}, or null: a printer "MYP-<n>-…" (also inside a bundle
   * "M-W+MYP-<n>-…") -> n × 400 prints, a season print bundle "M-W+MYP" -> 400; an extra print roll "XPR…" -> "+400 per roll".
   */
  function printInfoFor(sku) {
    var c = printInfoConfig();
    if (!c) return null;
    var s = String(sku || '').trim().toUpperCase();
    var out = {};
    // also a season print bundle "M-W+MYP" / "M-W+MYG+MYP" (owner 2026-10-08): one roll (400 prints) included
    var m = /(?:^|\+)MYP-(\d+)-/.exec(s) || /\+MYP(?=\+|$)/.exec(s);
    if (m) {
      out[c.key] = String(c.value).replace('__PRINTS__', String((+m[1] || 1) * (c.perRoll || 400)));
      return out;
    }
    if (/^XPR/.test(s) && c.rollKey) {
      out[c.rollKey] = c.rollValue;
      return out;
    }
    return null;
  }
  /** props + the print info of the SKU (props untouched when the SKU has none or the property is there). */
  function withPrintInfo(props, sku) {
    var info = printInfoFor(sku);
    if (!info) return props || null;
    var out = {};
    Object.keys(props || {}).forEach(function (k) { out[k] = props[k]; });
    Object.keys(info).forEach(function (k) { if (out[k] == null || out[k] === '') out[k] = info[k]; });
    return out;
  }
  /**
   * Right before checkout: every printer / bundle-with-printer / extra roll line without the property gets it
   * (change.js by line key, the other properties kept as they are). Never rejects; resolves the latest cart (or undefined).
   */
  function ensurePrintInfo(cart) {
    if (!printInfoConfig()) return Promise.resolve(cart);
    var fresh = cart && cart.items ? Promise.resolve(cart) : cartGet();
    return fresh.then(function (c) {
      var todo = (c.items || []).filter(function (it) {
        var info = printInfoFor(it.sku);
        if (!info) return false;
        return Object.keys(info).some(function (k) { var v = it.properties && it.properties[k]; return v == null || v === ''; });
      });
      var chain = Promise.resolve(c);
      todo.forEach(function (it) {
        chain = chain.then(function () {
          return cartPost('cart/change.js', { id: it.key, quantity: it.quantity, properties: withPrintInfo(it.properties, it.sku) });
        });
      });
      return chain;
    }).catch(function () { return cart; });
  }

  /**
   * What "Gå til betaling" adds (booking module and cart page): free props, one per booth (syncFreeProps), and the
   * free prop holder above the configured cart value (sections/main-cart.liquid rule).
   * The Upcart app (when present) adds the free props as a reward (~1-2 s after a cart change), so we wait for it
   * first; syncFreeProps then keeps one line with the right quantity.
   * c: {cameraType, freeProps: variant id | null, propHolder: {id, min} | null}. Never rejects.
   */
  function addAutomaticLines(c) {
    return addAutomaticLinesCore(c).then(function (cart) { return ensurePrintInfo(cart && cart.items ? cart : null); });
  }
  function addAutomaticLinesCore(c) {
    c = c || {};
    if (!c.freeProps && !c.propHolder) return Promise.resolve();
    var upcart = !!(window.upcart || window.upcartRegisterAddToCart);
    var freePropLines = function (cart) {
      return (cart.items || []).filter(function (it) { return it.variant_id === c.freeProps; });
    };
    // Upcart first: poll up to ~4 s for its reward line
    var waitForUpcart = function (cart, tries) {
      if (!upcart || !c.freeProps || !boothCount(cart, c) || freePropLines(cart).length || tries <= 0) return Promise.resolve(cart);
      return wait(500).then(cartGet).then(function (next) { return waitForUpcart(next, tries - 1); });
    };
    return cartGet()
      .then(function (cart) { return waitForUpcart(cart, 8); })
      .then(function (cart) { return syncFreeProps(cart, c); })
      .then(function (cart) {
        var items = cart.items || [];
        var has = function (id) { return items.some(function (it) { return it.variant_id === id; }); };
        if (!c.propHolder || has(c.propHolder.id)) return cart;
        var cameraValue = items.filter(function (it) { return it.product_type === c.cameraType; })
          .reduce(function (n, it) { return n + it.final_line_price; }, 0);
        var giftCards = items.filter(function (it) { return it.gift_card; }).reduce(function (n, it) { return n + it.final_line_price; }, 0);
        if (cart.total_price - cameraValue - giftCards < c.propHolder.min) return cart;
        return cartPost(QUIET_ADD, { items: [{ id: c.propHolder.id, quantity: 1 }] }).then(function () { return upcart ? wait(1500).then(cartGet) : cartGet(); });
      })
      // Upcart may still have added its reward meanwhile: one line, one unit per booth
      .then(function (cart) { return upcart ? syncFreeProps(null, c) : cart; })
      .catch(function () { /* never block the booking on the free props */ });
  }

  /* ---------- print type + "Logo på print" (owner 2026-10-05, _qa/bundles/plan.md) ----------
     Products in the selector JSON shape ({options: [names], variants: [{id, sku, options: [...]}]}).
     Print type keys: P = Polaroid, S = Strip / Fotostribe (the same thing). Option values win over SKU parsing. */

  /** 'P' | 'S' | '' for a print type name ("Polaroid", "Fotostribe", "Strip"). */
  function printKey(value) {
    var v = String(value || '');
    return /polaroid/i.test(v) ? 'P' : /strip|stribe/i.test(v) ? 'S' : '';
  }
  /** The line item property print_mode the backend reads (Myselfie.Orders): "Polaroid" | "Strip" | ''. */
  function printModeOf(type) {
    var k = printKey(type);
    return k === 'P' ? 'Polaroid' : k === 'S' ? 'Strip' : '';
  }
  function optsOf(v) {
    if (!v) return [];
    if (Array.isArray(v.options)) return v.options;
    return [v.option1, v.option2, v.option3].filter(function (x) { return x != null; });
  }
  function optName(o) { return typeof o === 'string' ? o : (o && o.name) || ''; }
  /** Index of the option whose values are print types (-1 = the product has no print type option). */
  function printTypeIndex(p) {
    if (!p || !p.options || !p.variants) return -1;
    for (var i = 0; i < p.options.length; i++) {
      for (var j = 0; j < p.variants.length; j++) if (printKey(optsOf(p.variants[j])[i])) return i;
    }
    return -1;
  }
  /** The value of a product's print type option that means the same as `type` ("Fotostribe" -> "Strip"), else `type`. */
  function printTypeValue(p, type) {
    var i = printTypeIndex(p);
    var k = printKey(type);
    if (i < 0 || !k) return type;
    for (var j = 0; j < p.variants.length; j++) {
      var val = optsOf(p.variants[j])[i];
      if (printKey(val) === k) return val;
    }
    return type;
  }
  function logoIndex(p) {
    if (!p || !p.options) return -1;
    var pt = printTypeIndex(p);
    for (var i = 0; i < p.options.length; i++) if (i !== pt && /logo/i.test(optName(p.options[i]))) return i;
    var k = window.myselfie && window.myselfie.seasonKit;
    var si = k ? k.seasonIndex(p) : -1;
    for (var j = 0; j < p.options.length; j++) if (j !== pt && j !== si) return j;
    return -1;
  }
  /** 'ML' (Myselfie Logo) | 'NL' (Ingen logo) | 'EL' (Eget logo) | '' for a logo variant: option value, else SKU. */
  function logoCode(v, p) {
    var li = logoIndex(p);
    var val = li > -1 ? String(optsOf(v)[li] || '') : '';
    if (/eget/i.test(val)) return 'EL';
    if (/ingen/i.test(val)) return 'NL';
    if (/myselfie/i.test(val)) return 'ML';
    var m = /^LOGO-(EL|NL|ML)\b/i.exec(String((v && v.sku) || '').trim());
    return m ? m[1].toUpperCase() : '';
  }
  /** 'P' | 'S' | '' (no print type: an old variant like LOGO-EL) for a logo variant. */
  function logoPrintKey(v, p) {
    var i = printTypeIndex(p);
    if (i > -1) return printKey(optsOf(v)[i]);
    var m = /^LOGO-(?:EL|NL|ML)-([PS])(?:-|$)/i.exec(String((v && v.sku) || '').trim());
    return m ? m[1].toUpperCase() : '';
  }
  /**
   * The logo variant for a logo code and a print type, in the season of a date: variants of that logo, then the
   * print type's (an old product without print types: any), then the season (assets/myselfie-seasons.js).
   * null when the product has no variant for that logo (e.g. no "Myselfie Logo" variant: no line).
   */
  function pickLogo(p, code, type, iso) {
    if (!p || !p.variants || !code) return null;
    var c = p.variants.filter(function (v) { return logoCode(v, p) === code; });
    if (!c.length) return null;
    var k = printKey(type);
    var keyed = c.filter(function (v) { return logoPrintKey(v, p) === k; });
    if (keyed.length) c = keyed;
    else if (k) {
      var loose = c.filter(function (v) { return !logoPrintKey(v, p); });
      if (loose.length) c = loose;
    }
    var avail = c.filter(function (v) { return v.available !== false; });
    if (avail.length) c = avail;
    var sk = window.myselfie && window.myselfie.seasonKit;
    return (sk ? sk.pick({ options: p.options, variants: c }, { iso: iso || '' }) : null) || c[0];
  }
  /** The admin name of a logo choice ("Myselfie Logo", "Ingen logo", "Eget logo"). */
  function logoName(p, code) {
    var li = logoIndex(p);
    var c = p && p.variants ? p.variants.filter(function (v) { return logoCode(v, p) === code; })[0] : null;
    var val = c && li > -1 ? String(optsOf(c)[li] || '') : '';
    if (val && !printKey(val)) return val;
    return { ML: 'Myselfie Logo', NL: 'Ingen logo', EL: 'Eget logo' }[code] || '';
  }

  /* ---------- order number ("Evt. tilhørende ordrenummer") ---------- */

  /**
   * Limits: the DK cart section's settings ("Mindste / Største ordrenummer", window.myselfie.orderNo), else
   * 1000-999999. limits: {min, max} overrides.
   */
  function orderNoLimits(limits) {
    var cfg = limits || (window.myselfie && window.myselfie.orderNo) || {};
    var min = Math.max(0, Math.floor(Number(cfg.min)));
    var max = Math.floor(Number(cfg.max));
    if (!isFinite(min) || cfg.min == null || cfg.min === '') min = 1000;
    if (!isFinite(max) || !max) max = 999999;
    if (max < min) max = min;
    return { min: min, max: max };
  }
  /** Digits only, between min and max (inclusive). */
  function validOrderNo(value, limits) {
    var v = String(value == null ? '' : value).trim().replace(/^#/, '');
    if (!/^\d+$/.test(v)) return false;
    var l = orderNoLimits(limits);
    var n = Number(v);
    return n >= l.min && n <= l.max;
  }
  /** The customer's message for a wrong order number. */
  function orderNoMessage(limits) {
    var l = orderNoLimits(limits);
    var f = function (n) { return String(n); };
    return 'Ordrenummeret skal være et tal mellem ' + f(l.min) + ' og ' + f(l.max) + ' – det står i jeres ordrebekræftelse.';
  }

  /* ---------- order-number rule (owner 2026-10-06) ---------- */

  /**
   * Config: window.myselfie.orderRule {min (øre; 0 = off), exempt [SKU prefixes], required [SKU prefixes], cameraType},
   * emitted by snippets/myselfie-cart-rules.liquid from the theme settings "Ordrenummer-regel" (DK only). null = no rule.
   */
  function orderRuleConfig() {
    var c = window.myselfie && window.myselfie.orderRule;
    if (!c) return null;
    var list = function (a) {
      return (Array.isArray(a) ? a : []).map(function (x) { return String(x == null ? '' : x).trim().toUpperCase(); }).filter(Boolean);
    };
    var min = Math.floor(Number(c.min));
    return { min: isFinite(min) && min > 0 ? min : 0, exempt: list(c.exempt), required: requiredRules(c.required), cameraType: c.cameraType || '' };
  }
  /**
   * "MYP:M-;G-EXT:@" -> [{prefix: 'MYP', partners: ['M-']}, {prefix: 'G-EXT', partners: ['@']}]. A rule without ":" has
   * no partners (always needs an order number). Partners are SKU beginnings (* = any text); "@" = the exempt list.
   */
  function requiredRules(v) {
    var parts = Array.isArray(v) ? v : String(v == null ? '' : v).split(';');
    return parts.map(function (r) {
      var i = String(r).indexOf(':');
      var prefix = String(i < 0 ? r : String(r).slice(0, i)).trim().toUpperCase();
      var partners = i < 0 ? [] : String(r).slice(i + 1).split(',').map(function (x) { return x.trim().toUpperCase(); }).filter(Boolean);
      return { prefix: prefix, partners: partners };
    }).filter(function (r) { return r.prefix; });
  }
  /** SKU begins like the pattern ("GAL-*-SA" matches "GAL-100-SA"). */
  function skuMatches(sku, pattern) {
    var esc = pattern.split('*').map(function (part) {
      return part.replace(/[.+?^$(){}|\[\]\\]/g, function (ch) { return '\\' + ch; });
    }).join('.*');
    return new RegExp('^' + esc).test(sku);
  }
  function skuOf(it) { return String((it && it.sku) || '').trim().toUpperCase(); }
  function hasPrefix(sku, list) {
    for (var i = 0; i < list.length; i++) if (sku.indexOf(list[i]) === 0) return list[i];
    return '';
  }
  function ruleFor(sku, rules) {
    for (var i = 0; i < rules.length; i++) if (sku.indexOf(rules[i].prefix) === 0) return rules[i];
    return null;
  }
  /** What the line cost before any discount (cart.js: original_line_price, øre). */
  function originalLinePrice(it) {
    if (typeof it.original_line_price === 'number') return it.original_line_price;
    if (typeof it.original_price === 'number') return it.original_price * (+it.quantity || 0);
    return linePrice(it);
  }
  function isRewardLine(it) {
    var p = it.properties || {};
    return Object.keys(p).some(function (k) { return k.indexOf('__upcart') === 0; });
  }
  /**
   * Does this cart need an order number ("ordrenummer" = the customer's existing Myselfie booking)?
   *   - "required" rules (settings; default "MYP:M-;G-EXT:@"): the line always needs one, unless a partner line is in
   *     the cart: a booth for the printer (Mygreetings / MyGallery do NOT unlock a printer); for the gallery extension
   *     "@" = every product of the exempt list (booth, Mygreetings, MyGallery stand-alone or add-on);
   *   - otherwise: cart value before discounts (incl. VAT) under the minimum, and no exempt line (a line that costs
   *     money whose SKU starts with an "exempt" prefix: booth, Mygreetings, MyGallery) or a gift card. 0 kr lines never exempt.
   * Returns {active, required, reason: '' | 'printer' | 'extension' | 'other' | 'value', value, min, skus}; value and
   * min in øre. Without the config (other stores) nothing is required. opts.config overrides the page config.
   */
  function orderRule(cart, opts) {
    var c = (opts && opts.config) || orderRuleConfig();
    var out = { active: false, required: false, reason: '', value: 0, min: 0, skus: [] };
    if (!c) return out;
    out.active = true;
    out.min = c.min;
    if (!cart || !cart.items || !cart.items.length) return out; // nothing to check out
    var exempt = false;
    var lines = []; // {sku, booth} of the lines that can be partners
    var hits = []; // {rule, sku}
    ((cart && cart.items) || []).forEach(function (it) {
      var qty = +it.quantity || 0;
      if (qty <= 0) return;
      var sku = skuOf(it);
      var price = originalLinePrice(it);
      out.value += price;
      if (isRewardLine(it)) return;
      var isBooth = /^M-/.test(sku) || (!!c.cameraType && it.product_type === c.cameraType);
      var gift = !!it.gift_card; // a gift card unlocks like an exempt product ("could be part of the gift"), but not a printer
      if (price > 0 && (isBooth || gift || hasPrefix(sku, c.exempt))) exempt = true;
      if (isBooth || price > 0) lines.push({ sku: sku, booth: isBooth, gift: gift });
      var rule = ruleFor(sku, c.required);
      if (rule) hits.push({ rule: rule, sku: sku });
    });
    var open = hits.filter(function (h) {
      return !lines.some(function (l) {
        return h.rule.partners.some(function (pt) {
          if (pt === '@') return !!hasPrefix(l.sku, c.exempt) || l.booth || l.gift; // any product that unlocks (the exempt list)
          return skuMatches(l.sku, pt) || (l.booth && pt === 'M-');
        });
      });
    });
    if (open.length) {
      out.required = true;
      out.skus = open.map(function (h) { return h.sku; });
      var pr = open[0].rule.prefix;
      out.reason = pr === 'MYP' || pr === 'QP' ? 'printer' : pr === 'G-EXT' ? 'extension' : 'other';
    } else if (c.min > 0 && out.value < c.min && !exempt) {
      out.required = true;
      out.reason = 'value';
    }
    return out;
  }
  /** "500 kr" for the minimum (øre in). */
  function orderRuleMin(rule) {
    var v = ((rule && rule.min) || 0) / 100;
    return v.toLocaleString('da-DK', { maximumFractionDigits: 2 }) + ' kr';
  }
  /** The customer's texts for a rule result: {label, message, notice}. '' reason = null. */
  function orderRuleText(rule) {
    var min = orderRuleMin(rule);
    switch (rule && rule.reason) {
      case 'value':
        return {
          label: 'Angiv ordrenummer eller tilføj varer for ' + min,
          notice: 'Ordrer under ' + min + ' kræver et ordrenummer fra jeres eksisterende Myselfie-booking.',
          message: 'Ordrer under ' + min + ' (før rabat) kræver et ordrenummer. Angiv ordrenummeret fra jeres eksisterende Myselfie-booking, eller læg flere varer i kurven, så den når mindst ' + min + '.',
        };
      case 'extension':
        return {
          label: 'Angiv ordrenummeret fra jeres booking',
          notice: 'Forlængelse af galleriet hører til en eksisterende booking og kræver ordrenummeret fra den – medmindre I også har en Myselfie, Mygreetings eller MyGallery i kurven.',
          message: 'Forlængelse af galleriet hører til en booking. Angiv ordrenummeret fra jeres eksisterende Myselfie-booking, eller læg en Myselfie, Mygreetings eller MyGallery i kurven.',
        };
      case 'other':
        return {
          label: 'Angiv ordrenummeret fra jeres booking',
          notice: 'Varerne i kurven kræver et ordrenummer fra jeres eksisterende Myselfie-booking.',
          message: 'Varerne i kurven hører til en booking. Angiv ordrenummeret fra jeres eksisterende Myselfie-booking.',
        };
      case 'printer':
        return {
          label: 'Tilføj en Myselfie eller jeres ordrenummer',
          notice: 'Printer sendes sammen med en Myselfie og kræver ordrenummeret fra jeres eksisterende Myselfie-booking, hvis I ikke booker en Myselfie nu.',
          message: 'Der er ingen Myselfie i kurven. Printer og rammer sendes sammen med en Myselfie – book en Myselfie, eller angiv ordrenummeret fra jeres eksisterende Myselfie-booking.',
        };
      default:
        return null;
    }
  }
  /**
   * Where "Gå til betaling" may go with this cart: the checkout, or the cart page when an order number is still missing
   * (the field and the message live there). cart: a full cart (cart.js or cart/update.js answer).
   */
  function checkoutAllowed(cart) {
    var rule = orderRule(cart);
    if (!rule.required) return true;
    var no = cart && cart.attributes && cart.attributes.orderNo;
    return validOrderNo(no);
  }

  /**
   * Season guard (assets/myselfie-seasons.js, _qa/seasons/spec.md), run right before checkout by the booking module
   * and the cart page: every line of a product with a "Sæson" option must carry the season of its date. A wrong one
   * is swapped for the right variant; when that changed a price, or could not be done, or a season line has no date
   * at all, the checkout stops with a message (the customer sees the new price and presses again).
   * Resolves the fix result (or null without the season kit); rejects {message, season: true}.
   */
  function ensureSeasons() {
    var sk = window.myselfie && window.myselfie.seasonKit;
    if (!sk) return Promise.resolve(null);
    return cartGet().then(sk.fixCart).then(function (r) {
      var blockers = sk.issues(r);
      if (blockers.length) throw { message: blockers[0].message, season: true, cart: r.cart };
      if (r.notes.length) throw { message: r.notes.join(' ') + ' Tjek prisen, og tryk igen.', season: true, cart: r.cart };
      return r;
    });
  }

  window.myselfie.checkoutKit = {
    ensureSeasons: ensureSeasons,
    QUIET_ADD: QUIET_ADD,
    rootUrl: rootUrl,
    wait: wait,
    cartJson: cartJson,
    cartPost: cartPost,
    cartGet: cartGet,
    validEan: validEan,
    eanProblem: eanProblem,
    billingIssues: billingIssues,
    billingData: billingData,
    reasonIssues: reasonIssues,
    reasonsFor: reasonsFor,
    termsIssue: termsIssue,
    termsDocument: termsDocument,
    checkoutAttributes: checkoutAttributes,
    noteFrom: noteFrom,
    checkoutUrl: checkoutUrl,
    addAutomaticLines: addAutomaticLines,
    syncFreeProps: syncFreeProps,
    freePropsOk: freePropsOk,
    FREE_PROPS_TAG: FREE_PROPS_TAG,
    boothCount: boothCount,
    isAutoLine: isAutoLine,
    paidCount: paidCount,
    cartDetail: cartDetail,
    serial: serial,
    orderNoLimits: orderNoLimits,
    validOrderNo: validOrderNo,
    orderNoMessage: orderNoMessage,
    printInfoFor: printInfoFor,
    withPrintInfo: withPrintInfo,
    stripPrintInfo: stripPrintInfo,
    ensurePrintInfo: ensurePrintInfo,
    orderRule: orderRule,
    orderRuleText: orderRuleText,
    orderRuleMin: orderRuleMin,
    checkoutAllowed: checkoutAllowed,
    printKey: printKey,
    printModeOf: printModeOf,
    printTypeIndex: printTypeIndex,
    printTypeValue: printTypeValue,
    logoCode: logoCode,
    logoPrintKey: logoPrintKey,
    pickLogo: pickLogo,
    logoName: logoName,
  };
})();
